import { useRef } from "react"

import { useTranslation } from "react-i18next"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

import { M365ConsentBody, M365ConsentHeading } from "./m365-consent-content"

type M365InfoModalProps = {
  open: boolean
  onApprove: () => void
  onDecline: () => void
  /** Non-persistent dismiss (overlay click, Esc). Falls back to onDecline if omitted. */
  onDismiss?: () => void
}

export function M365InfoModal({ open, onApprove, onDecline, onDismiss }: M365InfoModalProps) {
  const { t } = useTranslation()
  const closeIntentRef = useRef<"approve" | "decline" | "dismiss" | null>(null)

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) return
    if (closeIntentRef.current) {
      closeIntentRef.current = null
      return
    }
    // Overlay click / Esc — do not persist a decision unless the caller wants to.
    // If no onDismiss handler is provided, fall back to onDecline so the dialog
    // is always dismissible via overlay/Esc.
    ;(onDismiss ?? onDecline)()
  }

  const handleApprove = () => {
    closeIntentRef.current = "approve"
    onApprove()
  }

  const handleDecline = () => {
    closeIntentRef.current = "decline"
    onDecline()
  }

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <M365ConsentHeading />
          </AlertDialogTitle>
        </AlertDialogHeader>
        <AlertDialogBody>
          <M365ConsentBody />
        </AlertDialogBody>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleDecline}>
            {t("m365_tool.info.decline")}
          </AlertDialogCancel>
          <AlertDialogAction onClick={handleApprove}>
            {t("m365_tool.info.approve")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
